import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';

export type PageAccess = 'VIEW' | 'FULL';

export interface MyPermissions {
  isSuperUser: boolean;
  locationType: string | null;
  permissions: { key: string; access: PageAccess }[];
}

interface RouteRule {
  /** URL prefix (hash-router path, no query string). Longest prefix wins. */
  prefix: string;
  key: string;
  /** True when reaching the route mutates data (create/edit) → needs FULL. */
  full?: boolean;
}

/**
 * Maps frontend routes to RAC page keys (backend
 * src/access-control/page-registry.ts is the source of truth for the keys).
 * Kept central so app.routes.ts doesn't need a pageKey on every entry;
 * route `data: { pageKey, requiresFull }` overrides this table when present.
 */
const ROUTE_PAGE_MAP: RouteRule[] = [
  { prefix: '/product-list', key: 'products.list' },
  { prefix: '/add-product', key: 'products.list', full: true },
  { prefix: '/bulk-add-products', key: 'products.list', full: true },
  { prefix: '/bulk-ean-mapping', key: 'products.list', full: true },
  { prefix: '/edit-product', key: 'products.list', full: true },
  { prefix: '/product-vendor-mapping', key: 'products.vendor-mapping' },

  { prefix: '/vendors', key: 'vendors.list' },
  { prefix: '/add-vendor', key: 'vendors.list', full: true },
  { prefix: '/edit-vendor', key: 'vendors.list', full: true },
  { prefix: '/vendor-sku-upload', key: 'vendors.sku-upload' },
  { prefix: '/manufacturers', key: 'vendors.manufacturers' },
  { prefix: '/add-manufacturers', key: 'vendors.manufacturers', full: true },
  { prefix: '/edit-manufacturer', key: 'vendors.manufacturers', full: true },

  { prefix: '/b2b-orders', key: 'b2b.orders' },
  { prefix: '/b2b-orders/create', key: 'b2b.orders', full: true },
  { prefix: '/b2b-orders/edit', key: 'b2b.orders', full: true },
  { prefix: '/b2b-customer', key: 'b2b.customers' },
  { prefix: '/add-b2b-customer', key: 'b2b.customers', full: true },
  { prefix: '/edit-b2b-customer', key: 'b2b.customers', full: true },

  { prefix: '/billing/cart-audit', key: 'billing.cart-audit' },
  { prefix: '/billing', key: 'billing.pos' },
  { prefix: '/billinglist', key: 'reports.bill-summary' },
  { prefix: '/service-billing-details', key: 'reports.bill-summary' },

  { prefix: '/po', key: 'purchase.po' },
  { prefix: '/po/create-po', key: 'purchase.po', full: true },
  { prefix: '/po/edit-po', key: 'purchase.po', full: true },
  { prefix: '/prn', key: 'purchase.prn' },
  { prefix: '/prn/create', key: 'purchase.prn', full: true },
  { prefix: '/prn/edit', key: 'purchase.prn', full: true },

  { prefix: '/grn', key: 'grn.list' },
  { prefix: '/grn/create-grn', key: 'grn.list', full: true },
  { prefix: '/grn/edit', key: 'grn.list', full: true },

  { prefix: '/transfer-out', key: 'transfers.out' },
  { prefix: '/transfer-out/create', key: 'transfers.out', full: true },
  { prefix: '/transfer-out/edit', key: 'transfers.out', full: true },
  { prefix: '/transfer-out/eway-bill/create', key: 'transfers.eway-bill', full: true },
  { prefix: '/transfer-in', key: 'transfers.in' },
  { prefix: '/transfer-in/create', key: 'transfers.in', full: true },
  { prefix: '/transfer-in/edit', key: 'transfers.in', full: true },
  { prefix: '/transfer-box/print', key: 'transfers.box-print' },
  { prefix: '/transfer-box/trips', key: 'transfers.trips' },
  { prefix: '/transfer-box/dispatch-sheet', key: 'transfers.trips' },

  { prefix: '/inventory/list', key: 'stock.status' },
  { prefix: '/inventory/add', key: 'stock.status', full: true },
  { prefix: '/inventory/transfer', key: 'stock.status', full: true },
  { prefix: '/stocktake', key: 'stock.take' },
  { prefix: '/stocktake/create', key: 'stock.take', full: true },
  { prefix: '/stocktake/edit', key: 'stock.take', full: true },
  { prefix: '/stock-movement', key: 'stock.movement' },
  { prefix: '/stock-write-off', key: 'stock.write-off' },
  { prefix: '/stock-write-off/create', key: 'stock.write-off', full: true },
  { prefix: '/repack', key: 'stock.repack' },
  { prefix: '/repack/create-repack', key: 'stock.repack', full: true },
  { prefix: '/repack/edit', key: 'stock.repack', full: true },
  { prefix: '/kit-master', key: 'stock.kit-master' },
  { prefix: '/kit-preparation', key: 'stock.kit-preparation' },

  { prefix: '/mbq', key: 'mbq.drafts' },
  { prefix: '/mbq/upload', key: 'mbq.upload', full: true },
  { prefix: '/mbq/create-mbq', key: 'mbq.create', full: true },

  { prefix: '/indent-list', key: 'indent.list' },
  { prefix: '/indent-list/create-indent', key: 'indent.list', full: true },

  { prefix: '/picklist', key: 'picklist.master' },
  { prefix: '/picklist/create', key: 'picklist.master', full: true },
  { prefix: '/picklist/edit', key: 'picklist.master', full: true },

  { prefix: '/services', key: 'services.list' },
  { prefix: '/services/create', key: 'services.list', full: true },
  { prefix: '/services/edit', key: 'services.list', full: true },
  { prefix: '/services-billing', key: 'services.billing' },

  { prefix: '/offers-list', key: 'schemes.offers' },
  { prefix: '/create-offer', key: 'schemes.offers', full: true },
  { prefix: '/returns', key: 'schemes.returns' },
  { prefix: '/return-policy', key: 'schemes.return-policy' },

  { prefix: '/reports', key: 'reports.dashboard' },
  { prefix: '/detailed-report', key: 'reports.detailed' },
  { prefix: '/toola-sync', key: 'reports.toola-sync' },

  { prefix: '/terminals', key: 'terminals.monitor' },

  { prefix: '/users', key: 'controlpanel.users' },
  { prefix: '/users/create', key: 'controlpanel.users', full: true },
  { prefix: '/users/edit', key: 'controlpanel.users', full: true },
  { prefix: '/gst-slab', key: 'controlpanel.gst-slabs' },
  { prefix: '/gst-slab/create', key: 'controlpanel.gst-slabs', full: true },
  { prefix: '/gst-slab/edit', key: 'controlpanel.gst-slabs', full: true },
  { prefix: '/locations', key: 'controlpanel.locations' },
  { prefix: '/locations/create', key: 'controlpanel.locations', full: true },
  { prefix: '/locations/edit', key: 'controlpanel.locations', full: true },
  { prefix: '/reason-master', key: 'controlpanel.reason-master' },
  { prefix: '/roles-access', key: 'controlpanel.roles-access' },
  { prefix: '/configuration', key: 'controlpanel.configuration' },
];

/**
 * Effective page permissions for the logged-in user, fetched once per app
 * load from /access-control/my-permissions (the header's store switcher does
 * a full page reload, so a store change re-fetches with the new location
 * type). If the fetch fails, hasPermissionData() stays false and callers
 * (AuthGuard, NavigationService) fall back to the legacy role checks —
 * enabling RAC can therefore never lock everyone out.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private access: Map<string, PageAccess> | null = null;
  private isSuperUser = false;
  private pending$: Observable<boolean> | null = null;

  constructor() {
    // Drop cached permissions on logout so a re-login (possibly as another
    // user) fetches fresh data instead of serving the previous user's grants.
    this.authService.isLoggedIn$.subscribe((loggedIn) => {
      if (!loggedIn) this.clear();
    });
  }

  /** Resolves once permissions are fetched (or the fetch failed). */
  ensureLoaded(): Observable<boolean> {
    if (this.access) return of(true);
    if (!this.authService.isLoggedIn()) return of(false);
    if (this.pending$) return this.pending$;

    const locationType = this.authService.getActiveLocationType();
    let params = new HttpParams();
    if (locationType) params = params.set('locationType', locationType);
    this.pending$ = this.http
      .get<MyPermissions>(`${environment.apiUrl}/access-control/my-permissions`, { params })
      .pipe(
        tap((res) => {
          this.access = new Map(res.permissions.map((p) => [p.key, p.access]));
          this.isSuperUser = res.isSuperUser;
        }),
        map(() => true),
        catchError((err) => {
          console.error('Failed to load page permissions; falling back to role checks', err);
          this.pending$ = null;
          return of(false);
        }),
        shareReplay(1)
      );
    return this.pending$;
  }

  /** True when real permission data is available (not the legacy fallback). */
  hasPermissionData(): boolean {
    return this.access !== null;
  }

  canView(pageKey: string): boolean {
    if (!this.access) return true; // legacy fallback: roles decide
    return this.access.has(pageKey);
  }

  canEdit(pageKey: string): boolean {
    if (!this.access) return true;
    return this.access.get(pageKey) === 'FULL';
  }

  getAccess(pageKey: string): PageAccess | null {
    return this.access?.get(pageKey) ?? null;
  }

  isSuper(): boolean {
    return this.isSuperUser;
  }

  /** Longest-prefix match of a router URL (query string ignored) to a page key. */
  resolveRoute(url: string): { key: string; full: boolean } | null {
    const path = url.split('?')[0];
    let best: RouteRule | null = null;
    for (const rule of ROUTE_PAGE_MAP) {
      const matches =
        path === rule.prefix || path.startsWith(rule.prefix + '/');
      if (matches && (!best || rule.prefix.length > best.prefix.length)) {
        best = rule;
      }
    }
    return best ? { key: best.key, full: !!best.full } : null;
  }

  /** Drop cached permissions (used on logout). */
  clear(): void {
    this.access = null;
    this.isSuperUser = false;
    this.pending$ = null;
  }
}
